import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useReactToPrint } from "react-to-print";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "react-toastify";
import { Plus, Printer, Check } from "lucide-react";

import { fetchMasterPallete, createMasterPallete } from "./masterPalleteSlice";
import { listPaletteApi } from "../../auth/authService";
import PrintLabels from "./PrintLabels";
import {
    PageHeader,
    Button,
    SearchInput,
    Segmented,
    Field,
    inputClass,
    Modal,
    Pager,
    errorMessage
} from "../../components/MasterUi";

const PER_PAGE = 24;

// "PA0123" -> "Palet 123"
const suggestName = (code) => {
    const m = String(code).trim().match(/^PA0*(\d+)$/i);
    return m ? `Palet ${Number(m[1])}` : "";
};

const EMPTY_FORM = { code: "", bins_name: "" };

export default function MasterPalletePage() {
    const dispatch = useDispatch();
    const printRef = useRef();

    const masterPallete = useSelector((state) => state.masterPallete);
    const token = useSelector((state) => state.auth?.token);

    const data = masterPallete?.data || [];
    const pagination = masterPallete?.pagination || {};
    const loading = masterPallete?.loading || false;

    const [searchInput, setSearchInput] = useState("");
    const [filters, setFilters] = useState({ search: "", page: 1, per_page: PER_PAGE });

    const [printMode, setPrintMode] = useState("multi");
    const [selected, setSelected] = useState({}); // id -> item, tetap tersimpan lintas halaman
    const [printData, setPrintData] = useState([]);
    const [printing, setPrinting] = useState(false);

    const [openForm, setOpenForm] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [nameTouched, setNameTouched] = useState(false);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    // ambil data setiap filter berubah
    useEffect(() => {
        dispatch(fetchMasterPallete(filters));
    }, [dispatch, filters]);

    // pencarian dengan jeda, supaya tidak memanggil server di setiap ketukan
    useEffect(() => {
        const t = setTimeout(() => {
            setFilters((prev) => (prev.search === searchInput ? prev : { ...prev, search: searchInput, page: 1 }));
        }, 400);
        return () => clearTimeout(t);
    }, [searchInput]);

    const handlePrint = useReactToPrint({
        contentRef: printRef,
        documentTitle: "QR Palet",
        pageStyle: `
            @page { size: 101mm 53mm; margin: 0; }
            body { margin: 0; }
        `,
        onAfterPrint: () => setPrinting(false)
    });

    const selectedCount = Object.keys(selected).length;
    const totalAll = pagination?.total || 0;

    const handlePrintClick = async () => {
        try {
            setPrinting(true);
            let items;
            if (printMode === "multi") {
                if (
                    totalAll > 500 &&
                    !window.confirm(`Cetak ${totalAll.toLocaleString("id-ID")} label QR palet sekaligus? Proses ini bisa memakan waktu.`)
                ) {
                    setPrinting(false);
                    return;
                }
                // ambil semua hasil pencarian tanpa mengubah daftar di layar
                const res = await listPaletteApi(token, {
                    page: 1,
                    per_page: Math.max(totalAll, 1),
                    search: filters.search
                });
                items = res?.data || [];
            } else {
                items = Object.values(selected);
            }

            if (!items.length) {
                toast.info(printMode === "multi" ? "Tidak ada palet untuk dicetak." : "Pilih palet yang akan dicetak dulu.");
                setPrinting(false);
                return;
            }

            setPrintData(items);
            setTimeout(() => handlePrint(), 300);
        } catch (err) {
            setPrinting(false);
            toast.error("Data palet untuk dicetak tidak bisa diambil. Coba lagi.");
        }
    };

    const toggleSelect = (item) => {
        setSelected((prev) => {
            const next = { ...prev };
            if (next[item.id]) delete next[item.id];
            else next[item.id] = item;
            return next;
        });
    };

    const allOnPageSelected = data.length > 0 && data.every((d) => selected[d.id]);
    const togglePage = () => {
        setSelected((prev) => {
            const next = { ...prev };
            if (allOnPageSelected) data.forEach((d) => delete next[d.id]);
            else data.forEach((d) => (next[d.id] = d));
            return next;
        });
    };

    /* ===== form tambah ===== */

    const openCreate = () => {
        setForm(EMPTY_FORM);
        setErrors({});
        setNameTouched(false);
        setOpenForm(true);
    };

    const onCodeChange = (code) => {
        setForm((prev) => ({
            ...prev,
            code,
            bins_name: nameTouched ? prev.bins_name : suggestName(code)
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const errs = {};
        if (!form.code.trim()) errs.code = "Isi kode palet.";
        if (!form.bins_name.trim()) errs.bins_name = "Isi nama palet.";
        setErrors(errs);
        if (Object.keys(errs).length) return;

        try {
            setSaving(true);
            const res = await dispatch(
                createMasterPallete({ code: form.code.trim(), bins_name: form.bins_name.trim() })
            ).unwrap();
            toast.success(res?.message && res.message !== "success" ? res.message : `Palet ${form.code} ditambahkan.`);
            setOpenForm(false);
            dispatch(fetchMasterPallete(filters));
        } catch (err) {
            // tampilkan error validasi Laravel di field yang sesuai
            const m = err?.message;
            if (m && typeof m === "object") {
                setErrors({
                    code: m.code ? [].concat(m.code).join(" ") : undefined,
                    bins_name: m.bins_name ? [].concat(m.bins_name).join(" ") : undefined
                });
            }
            toast.error(errorMessage(err, "Palet gagal disimpan."));
        } finally {
            setSaving(false);
        }
    };

    const printLabel = useMemo(() => {
        if (printMode === "multi") return `Cetak QR (${totalAll.toLocaleString("id-ID")})`;
        return `Cetak QR (${selectedCount})`;
    }, [printMode, totalAll, selectedCount]);

    return (
        <div className="max-w-[1600px] mx-auto space-y-5">

            <PageHeader title="Master palet" subtitle="Daftar palet dan label QR untuk ditempel di palet">
                <Button variant="primary" onClick={openCreate} className="no-print">
                    <Plus size={16} /> Tambah palet
                </Button>
            </PageHeader>

            {/* ===== toolbar ===== */}
            <section className="bg-white rounded-lg border border-line p-4 flex flex-col lg:flex-row lg:items-center gap-3 no-print print:hidden">
                <SearchInput
                    value={searchInput}
                    onChange={setSearchInput}
                    placeholder="Cari kode atau nama palet"
                    className="lg:w-80"
                />

                <div className="flex flex-wrap items-center gap-3 lg:ml-auto">
                    <Segmented
                        label="Mode cetak"
                        value={printMode}
                        onChange={setPrintMode}
                        options={[
                            { value: "multi", label: filters.search ? "Semua hasil pencarian" : "Semua palet" },
                            { value: "single", label: "Pilih manual" }
                        ]}
                    />

                    <Button
                        variant="primary"
                        onClick={handlePrintClick}
                        disabled={printing || (printMode === "single" && selectedCount === 0)}
                    >
                        <Printer size={16} /> {printing ? "Menyiapkan…" : printLabel}
                    </Button>
                </div>
            </section>

            {printMode === "single" && (
                <div className="flex flex-wrap items-center gap-3 text-sm -mt-2">
                    <span className="text-muted">
                        <span className="font-semibold text-body">{selectedCount}</span> palet dipilih, tetap tersimpan walau pindah halaman.
                    </span>
                    <button type="button" onClick={togglePage} className="text-ink font-medium hover:underline">
                        {allOnPageSelected ? "Batalkan pilihan di halaman ini" : "Pilih semua di halaman ini"}
                    </button>
                    {selectedCount > 0 && (
                        <button type="button" onClick={() => setSelected({})} className="text-muted hover:text-body hover:underline">
                            Hapus semua pilihan
                        </button>
                    )}
                </div>
            )}

            {/* ===== grid label ===== */}
            <section className={`transition-opacity ${loading ? "opacity-60" : ""}`}>
                {data.length === 0 && !loading ? (
                    <div className="bg-white rounded-lg border border-dashed border-line px-6 py-14 text-center">
                        <div className="font-semibold text-body">
                            {filters.search ? "Palet tidak ditemukan" : "Belum ada palet"}
                        </div>
                        <p className="text-sm text-muted mt-1">
                            {filters.search
                                ? `Tidak ada palet dengan kode atau nama "${filters.search}".`
                                : "Tambahkan palet pertama untuk membuat label QR-nya."}
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3">
                        {data.map((item) => {
                            const isSelected = Boolean(selected[item.id]);
                            const selectable = printMode === "single";
                            const Tag = selectable ? "button" : "div";
                            return (
                                <Tag
                                    key={item.id}
                                    {...(selectable
                                        ? { type: "button", onClick: () => toggleSelect(item), "aria-pressed": isSelected }
                                        : {})}
                                    className={`relative text-left bg-white rounded-lg border p-3 flex items-center gap-4 transition-colors ${
                                        isSelected
                                            ? "border-ink ring-2 ring-ink/20"
                                            : selectable
                                                ? "border-line hover:border-ink/50"
                                                : "border-line"
                                    }`}
                                >
                                    <div className="shrink-0 bg-white p-1 rounded border border-line">
                                        <QRCodeCanvas value={item.code} size={84} />
                                    </div>

                                    <div className="min-w-0 flex-1">
                                        <div className="text-lg font-bold text-body tabular-nums leading-tight">{item.code}</div>
                                        <div className="text-sm text-muted truncate">{item.bins_name}</div>
                                    </div>

                                    {selectable && (
                                        <span
                                            className={`absolute top-2 right-2 w-5 h-5 rounded border flex items-center justify-center ${
                                                isSelected ? "bg-ink border-ink text-white" : "bg-white border-line"
                                            }`}
                                            aria-hidden="true"
                                        >
                                            {isSelected && <Check size={14} strokeWidth={3} />}
                                        </span>
                                    )}
                                </Tag>
                            );
                        })}
                    </div>
                )}
            </section>

            {totalAll > 0 && (
                <Pager
                    pagination={pagination}
                    unit="palet"
                    onChange={(page) => setFilters((prev) => ({ ...prev, page }))}
                />
            )}

            {/* area cetak tersembunyi */}
            <div style={{ position: "absolute", left: "-9999px", top: 0 }}>
                <PrintLabels ref={printRef} data={printData} />
            </div>

            {/* ===== modal tambah ===== */}
            <Modal
                open={openForm}
                onClose={() => !saving && setOpenForm(false)}
                title="Tambah palet"
                subtitle="Label QR akan dibuat dari kode palet."
                size="sm"
                footer={
                    <>
                        <Button onClick={() => setOpenForm(false)} disabled={saving}>Batal</Button>
                        <Button variant="primary" type="submit" form="form-palet" disabled={saving}>
                            {saving ? "Menyimpan…" : "Simpan palet"}
                        </Button>
                    </>
                }
            >
                <form id="form-palet" onSubmit={handleSubmit} className="space-y-4">
                    <Field label="Kode palet" hint="Contoh: PA5010. Dipakai sebagai isi QR." error={errors.code}>
                        <input
                            type="text"
                            autoFocus
                            value={form.code}
                            onChange={(e) => onCodeChange(e.target.value.toUpperCase())}
                            className={inputClass(errors.code)}
                        />
                    </Field>

                    <Field label="Nama palet" hint="Terisi otomatis dari kode, bisa diubah." error={errors.bins_name}>
                        <input
                            type="text"
                            value={form.bins_name}
                            onChange={(e) => {
                                setNameTouched(true);
                                setForm({ ...form, bins_name: e.target.value });
                            }}
                            className={inputClass(errors.bins_name)}
                        />
                    </Field>

                    {form.code && (
                        <div className="flex items-center gap-3 rounded-lg border border-line bg-surface/60 p-3">
                            <div className="bg-white p-1 rounded border border-line">
                                <QRCodeCanvas value={form.code} size={56} />
                            </div>
                            <div className="text-sm">
                                <div className="text-xs text-muted">Pratinjau label</div>
                                <div className="font-bold text-body">{form.code}</div>
                                <div className="text-muted">{form.bins_name || "-"}</div>
                            </div>
                        </div>
                    )}
                </form>
            </Modal>
        </div>
    );
}
